import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiResponse } from '@/types/api-types';
import type {
  QueryOptions,
  QuerySuspenseOptions,
} from '@/types/tanstack-query-options';
import type { paths } from '~/generated/inis/api-schema';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { inisApi } from '@/services';
import { myStationResSchema } from '~/generated/inis/zod-schema';

const API_PATH = '/v1/userStation/queryMyStation' as const;

type APIQueryResponse = ApiResponse<paths, typeof API_PATH>;

const apiResponseDataSchema = z.array(zodRemoveNullable(myStationResSchema));

type APIResponseData = z.infer<typeof apiResponseDataSchema>;

export type { APIResponseData as MyStations };

const queryFn = async (signal: AbortSignal) => {
  const { data: response } = await inisApi.get<APIQueryResponse>(API_PATH, {
    signal,
  });

  return apiResponseDataSchema.parse(response.data);
};

export const getSuspenseQueryMyStationOptions = () =>
  queryOptions({
    queryKey: [API_PATH] as const,
    queryFn: ({ signal }) => queryFn(signal),
  });

export const useQueryMyStation = (options?: QueryOptions<APIResponseData>) => {
  return useQuery({
    queryKey: [API_PATH],
    queryFn: ({ signal }) => queryFn(signal),
    ...options,
  });
};

export const useSuspenseQueryMyStation = (
  options?: QuerySuspenseOptions<APIResponseData, readonly [typeof API_PATH]>,
) => {
  return useSuspenseQuery({
    ...getSuspenseQueryMyStationOptions(),
    ...options,
  });
};
